import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Check, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FounderPioneerPage — public-facing claim flow for the first 500
 * subscribers. One year of Pro at $0 + lifetime "Founding Member"
 * badge that persists on the user's profile forever.
 *
 * The page itself is intentionally small + on-brand — it's not a
 * marketing landing page (the main pitch is on /), it's the claim
 * pad: status → CTA → confirmation.
 */
export default function FounderPioneerPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [status, setStatus] = useState(null); // { spots_left, is_founder, ... }
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const res = await axios.get(`${API}/promotions/founder/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setStatus(res.data);
      } catch {
        if (!cancelled) setStatus({ spots_left: null, is_founder: false });
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading]);

  const handleClaim = async () => {
    if (!user) {
      navigate('/login?redirect=/founder');
      return;
    }
    setClaiming(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const res = await axios.post(`${API}/promotions/founder/claim`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setStatus((s) => ({
        ...(s || {}),
        is_founder: true,
        founder_access_until: res.data.founder_access_until,
        spots_left: res.data.spots_left,
      }));
      if (refreshUser) await refreshUser();
      toast.success('Welcome to the Founder Pioneers');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not claim your spot');
    } finally {
      setClaiming(false);
    }
  };

  const spotsLeft = status?.spots_left;
  const isFounder = status?.is_founder;
  const accessUntil = status?.founder_access_until
    ? new Date(status.founder_access_until).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-12" data-testid="founder-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-xl w-full"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700]/12 border border-[#FFD700]/30 mb-6">
          <Crown className="w-3.5 h-3.5 text-[#FFD700]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-[#FFD700]">Founder Pioneer Program</span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-4">
          Be one of our first <span className="text-[#FFD700]">500 pioneers</span>.
        </h1>

        <p className="text-base text-white/70 leading-relaxed mb-8">
          Claim a full year of <strong className="text-white">iStudyPro Pro</strong> at $0 — and earn a
          Permanent <strong className="text-white">Founding Member</strong> badge on your profile.
          Forever. Even after the year ends.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {[
            { icon: Check, label: '365 days of Pro' },
            { icon: Check, label: 'Lifetime Founder badge' },
            { icon: Check, label: 'Exclusive early-access drops' },
            { icon: Check, label: 'Private feedback channel' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-sm text-white/85">
              <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Icon className="w-3 h-3 text-emerald-400" />
              </div>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {/* Status / CTA card */}
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6">
          {isFounder ? (
            <div data-testid="founder-success">
              <div className="inline-flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#FFD700]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[#FFD700]">You&apos;re in</span>
              </div>
              <h3 className="text-2xl font-semibold tracking-tight mb-1.5">Welcome, Pioneer.</h3>
              <p className="text-sm text-white/70 mb-5">
                Pro is active until <strong className="text-white">{accessUntil || '—'}</strong>.
                Your Founding Member badge is now visible on your profile.
              </p>
              <button
                onClick={() => navigate('/app')}
                className="w-full bg-[#FFD700] text-[#0A0A0A] hover:opacity-90 font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2"
                data-testid="founder-go-dashboard"
              >
                Go to your dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {spotsLeft !== null && spotsLeft !== undefined && (
                <p className="text-xs text-white/60 mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span data-testid="founder-spots-left"><strong className="text-white">{spotsLeft}</strong> of 500 spots remaining</span>
                </p>
              )}
              <button
                onClick={handleClaim}
                disabled={claiming || spotsLeft === 0}
                className="w-full bg-[#FFD700] text-[#0A0A0A] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                data-testid="founder-claim-btn"
              >
                {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                {claiming ? 'Claiming…' : (spotsLeft === 0 ? 'All spots claimed' : 'Claim my Founder spot')}
              </button>
              {!user && !authLoading && (
                <p className="text-xs text-white/50 mt-3 text-center">
                  You&apos;ll need to sign in or create an account first.
                </p>
              )}
            </>
          )}
        </div>

        <button
          onClick={() => navigate('/app')}
          className="mt-6 text-xs text-white/40 hover:text-white/70 transition"
        >
          ← Back to dashboard
        </button>
      </motion.div>
    </div>
  );
}
